import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight, MapPin, Check } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHead } from "../components/Motion";
import { FaqBlock, FinalCta, FinishTiers, HowItWorks, SliderShowcase } from "../components/Sections";
import { CITIES, cityBySlug } from "../data/cities";
import { img } from "../data/site";

const GTA_SERVICES = [
  { t: "Interior painting", to: "/residential/interior" },
  { t: "Exterior painting", to: "/residential/exterior" },
  { t: "Cabinet refinishing", to: "/residential/cabinets" },
  { t: "Condos & multi-unit", to: "/residential/cabinets" },
  { t: "Commercial painting", to: "/commercial" },
  { t: "Colour consultation", to: "/book" },
];
const COTTAGE_SERVICES = [
  { t: "Cottage exteriors & siding", to: "/residential/exterior" },
  { t: "Decks, docks & railings", to: "/residential/exterior" },
  { t: "Cottage interiors", to: "/residential/interior" },
  { t: "Kitchen & cabinet refinishing", to: "/residential/cabinets" },
  { t: "Boathouses & outbuildings", to: "/residential/exterior" },
  { t: "Colour consultation", to: "/book" },
];
const HERO_IMG = { gta: [img.livingTall, img.exterior, img.kitchen, img.livingWide], cottage: ["/images/cottage1.jpg", "/images/cottage3.jpg", "/images/cottage2.jpg"] };

const cityFaqs = (c) => {
  const cottage = c.region === "cottage";
  return [
    { q: `Do you serve all of ${c.name}?`, a: `Yes — including ${c.hoods.slice(0, -1).join(", ")} and ${c.hoods.at(-1)}. Book your free Colour & Scope Visit™ and Ilias comes to you.` },
    cottage
      ? { q: "Can you work while we're not at the cottage?", a: "Yes. We plan the job around your access dates, and Ilias walks you through the finished work in person or by phone before we call it done." }
      : { q: `What kinds of ${c.name} homes do you paint?`, a: `All of them — ${c.homes}. Ilias scopes each one in person, so your quote reflects your actual walls, not a guess.` },
    { q: "How do you keep the dust down?", a: "Every job runs on The Amora Dust-Free Promise™: sealed work zones, covered floors and furniture, vacuum-attached sanding and a clean-down at the end of every working day." },
    { q: "How fast will I get a quote?", a: "Within 24 hours of your free 30-minute visit — written, itemized and clear. No pressure, no obligation." },
  ];
};

const Neighbourhoods = ({ c }) => (
  <section className="bg-paper py-20 sm:py-28" data-testid="city-neighbourhoods">
    <div className="container-x grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionHead eyebrow={`Painters in ${c.name}`} title={c.region === "cottage" ? `Cottage painting across ${c.name}.` : `Every corner of ${c.name}.`} intro={c.blurb} />
      </div>
      <div className="lg:col-span-7">
        <p className="eyebrow text-slate-600">{c.region === "cottage" ? "Lakes & towns we travel to" : "Neighbourhoods we serve"}</p>
        <ul className="mt-5 grid gap-px overflow-hidden rounded-md bg-slate-200 sm:grid-cols-2">
          {c.hoods.map((h) => (
            <li key={h} className="flex items-center gap-3 bg-white p-5 font-display text-lg font-bold text-navy"><MapPin className="h-4 w-4 shrink-0 text-flame-600" />{h}</li>
          ))}
        </ul>
        <p className="mt-6 text-sm text-slate-600">We paint {c.homes} — and everything in between.</p>
      </div>
    </div>
  </section>
);

const CityServices = ({ c }) => (
  <section className="bg-white py-20 sm:py-28" data-testid="city-services">
    <div className="container-x">
      <SectionHead eyebrow="Services" title={`What we paint in ${c.name}.`} />
      <div className="mt-12 grid gap-px overflow-hidden rounded-md bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
        {(c.region === "cottage" ? COTTAGE_SERVICES : GTA_SERVICES).map((s, i) => (
          <Reveal key={s.t} delay={i * 0.04} className="bg-white">
            <Link to={s.to} className="group flex items-center justify-between gap-4 p-7" data-testid={`city-service-${i}`}>
              <span className="flex items-center gap-3 font-display text-lg font-bold text-navy"><Check className="h-5 w-5 text-flame-600" />{s.t}</span>
              <ArrowUpRight className="h-4 w-4 text-slate-500 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-flame-600" />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Nearby = ({ c }) => (
  <section className="bg-paper py-16" data-testid="city-nearby">
    <div className="container-x flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
      <p className="font-display text-2xl font-bold text-navy">Also serving nearby</p>
      <div className="flex flex-wrap gap-3">
        {c.nearby.map(cityBySlug).filter(Boolean).map((n) => (
          <Link key={n.slug} to={`/painters/${n.slug}`} className="btn-ghost-light !py-2.5" data-testid={`city-nearby-${n.slug}`}>{n.name.replace(/^the /, "The ")}</Link>
        ))}
        <Link to="/service-areas" className="btn-ghost-light !py-2.5" data-testid="city-all-areas-link">All service areas</Link>
      </div>
    </div>
  </section>
);

export function CityPage() {
  const { slug } = useParams();
  const c = cityBySlug(slug);
  if (!c) return <Navigate to="/service-areas" replace />;
  const cottage = c.region === "cottage";
  const label = c.name.replace(/^the /, "The ");
  const pool = HERO_IMG[c.region];
  const hero = pool[CITIES.indexOf(c) % pool.length];
  return (
    <>
      <Seo
        title={cottage ? `Cottage Painters in ${label} | Exterior, Decks & Docks | Amora Painting` : `Painters in ${label} | Dust-Free House Painting | Amora Painting`}
        description={`${cottage ? "Cottage painting" : "Residential and commercial painting"} in ${c.name}: ${c.hoods.slice(0, 3).join(", ")} and more. 30 years, licensed & insured. Free Colour & Scope Visit, written quote within 24 hours.`}
      />
      <PageHero eyebrow={label} crumbs={[{ label: "Service areas", to: "/service-areas" }]}
        lines={cottage ? ["Cottage painting", `in ${c.name}.`] : ["Painters in", `${label}.`]}
        intro={`Speed and quality, never a compromise. 30 years painting ${c.homes} — dust-free, on time and done right.`}
        image={hero} alt={`Painted home in ${c.name} by Amora Painting`} />
      <Neighbourhoods c={c} />
      <CityServices c={c} />
      <HowItWorks />
      <SliderShowcase />
      <FinishTiers />
      <FaqBlock faqs={cityFaqs(c)} title={`${label} FAQ.`} />
      <Nearby c={c} />
      <FinalCta />
    </>
  );
}

const CityGroup = ({ title, list }) => (
  <Reveal>
    <p className="eyebrow text-flame-600">{title}</p>
    <ul className="mt-6 grid gap-px overflow-hidden rounded-md bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
      {list.map((c) => (
        <li key={c.slug} className="bg-white">
          <Link to={`/painters/${c.slug}`} className="group flex items-center justify-between p-6" data-testid={`area-link-${c.slug}`}>
            <span className="font-display text-xl font-bold text-navy">{c.name.replace(/^the /, "The ")}</span>
            <ArrowUpRight className="h-4 w-4 text-slate-500 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-flame-600" />
          </Link>
        </li>
      ))}
    </ul>
  </Reveal>
);

export function ServiceAreasPage() {
  return (
    <>
      <Seo title="Service Areas | Painters Across the GTA & Ontario Cottage Country | Amora Painting" description="Amora Painting serves all of the GTA — Toronto, Mississauga, Vaughan, Markham, Oakville and more — plus Muskoka, the Kawarthas, Haliburton and Georgian Bay. Commercial across Ontario." />
      <PageHero eyebrow="Service areas" lines={["All of the GTA.", "Cottage country.", "Ontario-wide."]} intro="Find your city for local details, neighbourhoods we serve and how to book your free Colour & Scope Visit™. Commercial projects anywhere in Ontario." />
      <section className="bg-paper py-20 sm:py-28" data-testid="service-areas-index">
        <div className="container-x space-y-16">
          <CityGroup title="Greater Toronto Area" list={CITIES.filter((c) => c.region === "gta")} />
          <CityGroup title="Ontario cottage country" list={CITIES.filter((c) => c.region === "cottage")} />
          <Reveal className="rounded-md bg-navy p-8 text-white sm:p-10">
            <p className="eyebrow text-flame">Commercial</p>
            <p className="mt-4 max-w-2xl font-display text-2xl font-bold">Commercial projects across all of Ontario — offices, hospitality, retail, high-rise and multi-unit buildings.</p>
            <Link to="/commercial" className="btn-primary mt-6" data-testid="areas-commercial-link">Explore commercial</Link>
          </Reveal>
        </div>
      </section>
      <FinalCta />
    </>
  );
}
